import React, { useState, useEffect } from 'react';
import { useAthena } from '../useAthena';
import { format, addMonths, subMonths } from 'date-fns';

interface UseFetchAvailableDatesResult {
  availableDates: number[];
  loadingAvailableDates: boolean;
  error: string | null;
}

export const useFetchAvailableDates = (
  day: number | null | undefined,
  month: number,
  year: number,
  onMonthChange: (newDate: Date) => void,
): UseFetchAvailableDatesResult => {
  const [availableDates, setAvailableDates] = useState<number[]>([]);
  const [loadingAvailableDates, setLoadingAvailableDates] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const athena = useAthena();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchMonth = month + 1;
  // console.log(month);
  const fetchYear = year;
  // console.log(year);
  const today = new Date(year, month, day || 1);

  const handleNextMonth = () => {
    onMonthChange(addMonths(today, 1));
  };

  useEffect(() => {
    if (import.meta.env.DEV) {
      setLoadingAvailableDates(false);
      setError(null);
      setAvailableDates([2, 5, 8, 12, 16, 19, 23, 27]);
      return;
    }

    const fetchAvailableDates = async () => {
      if (!fetchMonth || !fetchYear) return;

      setLoadingAvailableDates(true);
      setError(null);

      try {
        const response = await fetch(
          `${
            import.meta.env.VITE_API_URL
          }/api/consultation/available-dates?month=${
            month + 1
          }&year=${year}&time_zone=${timeZone}`,
          {
            method: 'GET',
            credentials: 'include',
          },
        );

        console.log(response);

        if (!response.ok) {
          const message = `Server error: ${response.status},`;

          setError(message);
          setAvailableDates([]);

          setLoadingAvailableDates(false);

          athena.throwError({
            status: response.status,
            message,
          });

          return;
        }

        const data = await response.json();

        if (data.availableDates.length === 0) {
          handleNextMonth();
        }

        setAvailableDates(data.availableDates);
        setLoadingAvailableDates(false);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        athena.throwError({ status: 500, message });
        setError(message);
        console.error('Error fetching available dates:', error);
        setAvailableDates([]);
        setLoadingAvailableDates(false);
      }
    };

    fetchAvailableDates();
  }, [month, year]);

  return { availableDates, loadingAvailableDates, error };
};
